import store from '@/store/store'
import { logger } from '@logger'
import { notify, gameToasts } from '@/core/toast'
import { renderValue } from '@utils/numberUtils'
import { getGlobalParams } from '@core/graphQL/globalParams'
import { GAMES } from '@core/appearance/games'
import { shareWin } from '@core/chat/chatClient'
import { emitPlaceBet } from '@sockets/socketEmitters'
import { GAME_STATUS } from '@sockets/socketEvents'
import {
  BET_STATE,
  BET_MODE,
  TOGGLE,
  AUTOBET_CYCLE,
  BET_SUBTYPE,
  BET_RESULT,
  PANELS,
  PANEL_IDS,
  PLACE_BET_ERROR_ACTION,
} from '@constants/bettingConstants'
import { classifyPlaceBetError } from '@core/betting/betErrors'
import { submitClientSeedForCurrentRound } from '@core/provablyFair/clientSeed'
import { isSubmissionDue } from '@core/betting/betSubmissionWindow'
import {
  queueBet,
  markSubmitted,
  clearPending,
  acceptBet,
  markRunning,
  settleCashout,
  markCrashed,
  resetBet,
  setMode,
  setAutoBetCycle,
} from '@slices/bettingSlice'

/**
 * The single writer for Classic bet lifecycle. Framework-agnostic (plain
 * module) so it works even when the panel is unmounted and never races a second
 * writer. `socketListeners` calls the round-phase + command-reply hooks; the
 * scheduler (and a late press, via the hook) calls `tryEmitPanel`.
 *
 * Betting is DEFERRED and one-way. Pressing Bet only queues the bet in frontend
 * state (BET_PLACED); `tryEmitPanel` withholds PLACE_BET until the submission
 * window is due — shortly before the round takes off. The instant it is emitted
 * the panel moves to BET_SUBMITTED and the bet is FINAL: there is no cancel and
 * no rollback path, only the server's verdict.
 *
 * Server contract facts it relies on: bets are accepted ONLY in ACCEPT_BET;
 * the reply is PLACE_BET_RESPONSE with a `transactionId` and NO `panel`; there
 * is no client idempotency key — so we correlate replies here.
 */
const MODULE = 'Classic Betting'

// FIFO of bets emitted this round but not yet matched to a PLACE_BET_RESPONSE.
// Because the BE never echoes `panel`, we resolve the reply's panel from here.
// CASHOUT/SETTLED carry transactionId and are matched directly instead.
let pendingEmits = []

const state = () => store.getState()
const dispatch = (action) => store.dispatch(action)
const panels = () => state().betting.classic.panels
const round = () => state().events.round
const balance = () => state().wallet.balance

// ── correlation ─────────────────────────────────────────────────────────────
// Prefer the server-echoed panel if/when the BE adds it (forward-compatible);
// otherwise FIFO-match against our own emit queue, filtered by roundId.
const resolvePanel = (data) => {
  if (data?.panel === PANELS.ONE || data?.panel === PANELS.TWO) return data.panel
  const roundId = data?.roundId
  const idx = pendingEmits.findIndex((e) => e.roundId == null || e.roundId === roundId)
  const entry = idx === -1 ? pendingEmits.shift() : pendingEmits.splice(idx, 1)[0]
  return entry?.panel ?? null
}

const panelByTxn = (transactionId) => {
  if (!transactionId) return null
  const p = panels()
  return PANEL_IDS.find((id) => p[id]?.transactionId === transactionId) ?? null
}

// Whether we're awaiting a PLACE_BET_RESPONSE. socketListeners routes a
// place-reply to whichever orchestrator (Classic vs Freebet) has a pending emit
// — only one can, because the two tabs are mutually exclusive while busy.
export const hasPendingEmit = () => pendingEmits.length > 0

const stopAuto = (panel, reason) => {
  if (panels()[panel].mode !== BET_MODE.AUTO) return
  dispatch(setAutoBetCycle({ panel, cycle: AUTOBET_CYCLE.IDLE }))
  dispatch(setMode({ panel, mode: BET_MODE.MANUAL }))
  if (reason) notify('warning', { title: 'Auto-bet stopped', sub: reason })
}

// Win pop-up — formats outside React using the store's conventions. When chat is
// enabled it also offers a "Share" button in the toast; the win is shared to
// chat ONLY when the player taps it (never automatically). The bet amount is
// captured here (settleCashout preserves the stake) so the share reflects this
// round even if the panel later resets.
const notifyWin = (panel, payout, payoutMultiplier) => {
  const amount = renderValue({ value: payout, conventions: state().conventions })
  const multiplier =
    payoutMultiplier != null ? `${Number(payoutMultiplier).toFixed(2)}x` : undefined

  const chatEnabled = Boolean(state().configurations?.featureToggles?.chat)
  const { gameCode } = getGlobalParams()
  const betAmount = Number(panels()[panel]?.stake) || 0

  const onShare = chatEnabled
    ? () =>
        shareWin({
          gameName: GAMES[gameCode]?.gameName,
          cashoutMultiplier: payoutMultiplier,
          cashoutAmount: payout,
          betAmount,
        })
    : undefined

  gameToasts.cashout({ amount, multiplier, onShare })
}

// A panel is free to take a new bet only when nothing is live on it: idle, or
// already settled/crashed this round (the controls treat both alike).
const canAcceptNewBet = (betState) =>
  betState === BET_STATE.IDLE ||
  betState === BET_STATE.BET_CASHED_OUT ||
  betState === BET_STATE.CRASHED

// ── player intent ────────────────────────────────────────────────────────────
// Both entry points below re-read LIVE store state rather than trusting what the
// card rendered. That matters at the submission deadline: the scheduler can fire
// between a render and the click it produced, so a press must never queue a
// second bet over one already sent, nor withdraw a bet that has already gone out.

/**
 * Store a pending bet from a player press. Nothing reaches the server here unless
 * the press itself lands inside the final submission window.
 */
export const placePanelBet = (panel) => {
  const p = panels()[panel]
  console.log('BETDEBUG-1: Bet button click received', { panel, betState: p?.betState })
  if (!p || !canAcceptNewBet(p.betState)) {
    console.log('BETDEBUG-1x: ABORTED — panel missing or a bet is already in flight on it', {
      panel,
      betState: p?.betState,
    })
    return
  }
  dispatch(queueBet({ panel }))

  // Provably fair v2: the player's contribution to this round's outcome. Sent
  // here because pressing Bet is the moment the player opts in, and deliberately
  // NOT awaited — it is fire-and-forget, cannot throw, and nothing below reads its
  // result. If it fails the round seals with a server fallback instead.
  submitClientSeedForCurrentRound()

  console.log('BETDEBUG-2: Bet queued locally (pending), attempting emit', { panel })
  tryEmitPanel(panel)
}

/**
 * Discard a pending bet. Only ever touches a bet that exists solely in frontend
 * state — a submitted bet is final and this becomes a no-op.
 */
export const cancelPendingBet = (panel) => {
  const p = panels()[panel]
  if (!p) return
  if (p.betState === BET_STATE.BET_PLACED) {
    dispatch(resetBet({ panel }))
    return
  }
  // Nothing pending. If a bet is live, the press raced the submission — the bet
  // is final, so there is deliberately nothing to undo.
  if (!canAcceptNewBet(p.betState)) {
    logger.info(MODULE, 'Cancel ignored — bet already submitted', panel)
  }
}

// ── emit ─────────────────────────────────────────────────────────────────────
// Sends a pending bet IF the round is accepting AND the submission window is due
// (the final seconds before takeoff). Safe to call anytime: it no-ops otherwise,
// so the bet simply stays pending — in frontend state, still the player's — until
// the scheduler fires. This is the ONE place a Classic bet becomes irreversible.
export const tryEmitPanel = (panel) => {
  const p = panels()[panel]
  console.log('BETDEBUG-context: tryEmitPanel invoked, current state snapshot', {
    panel,
    exists: !!p,
    betState: p?.betState,
    roundStatus: round().status,
    isSubmissionDue: isSubmissionDue(),
    stake: p?.stake,
    balance: balance(),
    now: Date.now(),
  })
  if (!p) {
    console.log('BETDEBUG-7-FAIL: no panel state at all', { panel })
    return
  }
  if (round().status !== GAME_STATUS.ACCEPT_BET) {
    console.log('BETDEBUG-7-FAIL: round is not in ACCEPT_BET (bet stays queued)', {
      panel,
      roundStatus: round().status,
    })
    return // wait for the window
  }
  if (!isSubmissionDue()) {
    console.log('BETDEBUG-7-FAIL: submission window not due yet (bet stays queued)', { panel })
    return // hold the bet — not yet time to send
  }
  if (p.betState !== BET_STATE.BET_PLACED) {
    console.log('BETDEBUG-7-FAIL: nothing pending on this panel', {
      panel,
      betState: p.betState,
    })
    return // nothing pending, or already sent
  }

  const stake = Number(p.stake)
  if (!stake || stake <= 0) {
    console.log('BETDEBUG-7-FAIL: invalid stake, bet dropped', { panel, stake: p.stake })
    dispatch(resetBet({ panel }))
    return
  }
  if (balance() < stake) {
    console.log('BETDEBUG-7-FAIL: insufficient balance, bet dropped', {
      panel,
      stake,
      balance: balance(),
    })
    stopAuto(panel, 'Insufficient balance')
    dispatch(resetBet({ panel }))
    return
  }

  const { currency, gameCode } = getGlobalParams()
  if (!currency) {
    console.log('BETDEBUG-7-FAIL: currency missing from global params, bet dropped', {
      panel,
      globalParams: getGlobalParams(),
    })
    logger.error(MODULE, 'Cannot place bet: currency missing')
    dispatch(resetBet({ panel }))
    return
  }

  console.log('BETDEBUG-7: all guards passed — round open, window due, stake/balance/currency OK', {
    panel,
  })

  const target = Number(p.autoCashout.value)
  const cashOutAt = p.autoCashout.status === TOGGLE.ENABLED && target > 1 ? target : 0
  const subType = p.mode === BET_MODE.AUTO ? BET_SUBTYPE.AUTO : BET_SUBTYPE.MANUAL

  // Second, guarded attempt. The press above can land while the PREVIOUS round is
  // still running, in which case its seed was aimed at a round that had already
  // closed; this line always runs inside ACCEPT_BET, so it is the one that lands
  // for a queued or auto bet. The per-round guard inside collapses the pair to a
  // single request in the normal case.
  submitClientSeedForCurrentRound()

  pendingEmits.push({ panel, roundId: round().roundId })
  dispatch(markSubmitted({ panel }))

  console.log('BETDEBUG-8: emitting PLACE_BET to server', {
    panel,
    betAmount: stake,
    currency,
    cashOutAt,
    subType,
    gameCode,
    roundId: round().roundId,
  })

  const ok = emitPlaceBet({
    betAmount: stake,
    currency,
    cashOutAt,
    panel,
    subType,
    source: null,
    freebetId: null,
    sourceId: null,
    jackpotRatio: null,
    gameCode,
  })

  if (ok) {
    console.log('BETDEBUG-9: socket accepted the emit (sent over the wire)', { panel })
  } else {
    console.log('BETDEBUG-9-FAIL: socket was disconnected, emit never left the client', { panel })
  }

  if (!ok) {
    // The emit never left the client (socket down), so no bet exists anywhere —
    // return it to pending so the player keeps it for the next round.
    pendingEmits = pendingEmits.filter((e) => e.panel !== panel)
    dispatch(queueBet({ panel }))
  }
}

// ── round-phase hooks (called from socketListeners) ──────────────────────────
export const onScheduled = () => {
  pendingEmits = [] // a fresh round — nothing from the last one can still match
  PANEL_IDS.forEach((panel) => {
    const s = panels()[panel].betState
    if (s === BET_STATE.CRASHED || s === BET_STATE.BET_CASHED_OUT) dispatch(resetBet({ panel }))
  })
}

// Re-arms the auto loop for this round. It only QUEUES — the actual send is the
// scheduler's job, which socketListeners arms right after this so a freshly
// queued auto bet is picked up by the same window.
export const onAcceptBet = () => {
  PANEL_IDS.forEach((panel) => {
    const p = panels()[panel]
    // auto re-arm: queue again every round while the loop is active
    if (
      p.mode === BET_MODE.AUTO &&
      p.autoBet.cycle === AUTOBET_CYCLE.ACTIVE &&
      p.betState === BET_STATE.IDLE
    ) {
      if (balance() < Number(p.stake)) stopAuto(panel, 'Insufficient balance')
      else dispatch(queueBet({ panel }))
    }
  })
}

export const onRunning = () => {
  PANEL_IDS.forEach((panel) => {
    if (panels()[panel].betState === BET_STATE.BET_ACCEPTED) dispatch(markRunning({ panel }))
  })
}

export const onEnded = () => {
  PANEL_IDS.forEach((panel) => {
    const p = panels()[panel]
    if (p.betState === BET_STATE.BET_RUNNING) dispatch(markCrashed({ panel }))
    // A submitted bet whose PLACE_BET_RESPONSE never arrived: the round is over
    // and we hold no transactionId, so there is nothing left to reconcile. Free
    // the panel rather than strand it — BET_SETTLED can no longer match it.
    if (p.betState === BET_STATE.BET_SUBMITTED) {
      logger.warn(MODULE, 'Round ended with no place-bet reply — releasing panel', panel)
      dispatch(resetBet({ panel }))
    }
    // recover the UI if a cashout reply was lost — the authoritative BET_SETTLED
    // will still correct the result if it was actually a win
    if (p.pending) dispatch(clearPending({ panel }))
  })
}

// ── command replies (called from socketListeners extension points) ───────────
export const onPlaceBetResponse = (msg) => {
  console.log('BETDEBUG-10: PLACE_BET_RESPONSE received from server', msg)
  const panel = resolvePanel(msg?.data)
  if (!panel) {
    console.log('BETDEBUG-10-FAIL: response could not be matched to a panel (ignored)', msg?.data)
    return // not ours (e.g. a freebet) or unmatched — ignore
  }

  if (msg?.success) {
    const { transactionId, roundId } = msg.data || {}
    console.log('BETDEBUG-10-SUCCESS: bet accepted by server — DONE', { panel, transactionId, roundId })
    dispatch(acceptBet({ panel, transactionId, roundId }))
    return
  }

  console.log('BETDEBUG-10-REJECTED: server rejected the bet', { panel, code: msg?.code, message: msg?.message })
  const action = classifyPlaceBetError(msg?.code)
  if (action === PLACE_BET_ERROR_ACTION.RETRY) {
    // Round wasn't open — nothing was staked, so the bet goes back to pending
    // (the player's again) and is submitted in the next round's window.
    dispatch(queueBet({ panel }))
    logger.info(MODULE, 'Bet not accepted, held for next round', msg?.code)
  } else {
    dispatch(resetBet({ panel }))
    stopAuto(panel) // a hard failure stops the auto loop
    notify('error', { title: 'Bet failed', sub: msg?.message })
  }
}

export const onCashoutResponse = (msg) => {
  const panel = panelByTxn(msg?.data?.transactionId)
  if (!panel) return
  if (msg?.success) {
    dispatch(settleCashout({ panel, cashoutAmount: msg.data?.payout ?? null }))
    notifyWin(panel, msg.data?.payout, msg.data?.payoutMultiplier)
  } else {
    dispatch(clearPending({ panel }))
    notify('error', { title: 'Cashout failed', sub: msg?.message })
  }
}

export const onBetSettled = (msg) => {
  // Authoritative final record. This is the safety net for the currently-broken
  // CASHOUT_RESPONSE: it corrects a panel even if onEnded already marked it
  // CRASHED, so a genuine win still shows. It never double-toasts a win that a
  // CASHOUT_RESPONSE already settled.
  const panel = panelByTxn(msg?.data?.transactionId)
  if (!panel) return
  const current = panels()[panel].betState
  if (current === BET_STATE.IDLE) return // no live bet to reconcile

  const data = msg?.data || {}
  const payout = Number(data.payout) || 0
  const won = data.result === BET_RESULT.CASHED_OUT || data.result === BET_RESULT.WON || payout > 0

  if (won) {
    if (current !== BET_STATE.BET_CASHED_OUT) {
      dispatch(settleCashout({ panel, cashoutAmount: payout }))
      notifyWin(panel, payout, data.payoutMultiplier)
    }
  } else if (current !== BET_STATE.CRASHED) {
    dispatch(markCrashed({ panel }))
  }
  if (panels()[panel].pending) dispatch(clearPending({ panel }))
}
